import { Injectable } from '@angular/core';

import { guid } from '@fry/lib/utils';

import { EditorEligibility } from '../editor-eligibility/editor-eligibility.model';
import { EditorEligibilityTree } from './editor-eligibility-tree.model';
import { EditorEligibilityOperator } from '../editor-eligibility-operator/editor-eligibility-operator.model';
import { EditorEligibilityRule } from '../editor-eligibility-rule/editor-eligibility-rule.model';
import { EditorEligibilityRuleHasRole } from '../editor-eligibility-rule/editor-eligibility-rule-has-role.model';
import { EditorEligibilityRuleHasRelation } from '../editor-eligibility-rule/editor-eligibility-rule-has-relation.model';
import { EditorEligibilityRuleHasBookingItem } from '../editor-eligibility-rule/editor-eligibility-rule-has-booking-item.model';
import { EditorEligibilityRuleHasBookingOption } from '../editor-eligibility-rule/editor-eligibility-rule-has-booking-option.model';
import { EditorEligibilityRuleHasBookingType } from '../editor-eligibility-rule/editor-eligibility-rule-has-booking-type.model';
import { EditorEligibilityRuleHasEvent } from '../editor-eligibility-rule/editor-eligibility-rule-has-event.model';
import { EditorEligibilityRuleHasEventCount } from '../editor-eligibility-rule/editor-eligibility-rule-has-event-count.model';
import { EditorEligibilityRuleHasDateUserField } from '../editor-eligibility-rule/editor-eligibility-rule-has-date-user-field.model';
import { EditorEligibilityRuleHasDiscreteUserField } from '../editor-eligibility-rule/editor-eligibility-rule-has-discrete-user-field.model';
import { EditorEligibilityRuleHasStringUserField } from '../editor-eligibility-rule/editor-eligibility-rule-has-string-user-field.model';
import { EditorEligibilityRuleIsRawOperator } from '../editor-eligibility-rule/editor-eligibility-rule-is-raw-operator.model';


@Injectable()
export class EditorEligibilityTreeService {
  constructor() {

  }

  getEmptyEligibilityTree(): EditorEligibilityTree {
    const rootOperator: EditorEligibilityOperator = EditorEligibilityOperator.createDefault();
    const ruleList: EditorEligibilityRule[] = [null];
    rootOperator.ruleIndex = 0;
    return new EditorEligibilityTree({ rootOperator, ruleList });
  }

  getEligibilityTreeFromEligibility(eligibility: EditorEligibility): EditorEligibilityTree {
    const ruleList: EditorEligibilityRule[] = [];
    const operatorWithRuleList = this.parseEligibilityOperatorFromEligibilityRules(
      JSON.parse(eligibility.rules),
      null,
      ruleList
    );
    operatorWithRuleList.operator.name = eligibility.name;
    return new EditorEligibilityTree({
      rootOperator: operatorWithRuleList.operator,
      ruleList: operatorWithRuleList.ruleList
    });
  }

  parseEligibilityOperatorFromEligibilityRules(
    eligibilityRules: any,
    parentOperator: EditorEligibilityOperator,
    ruleList: EditorEligibilityRule[]
  ): { operator: EditorEligibilityOperator, ruleList: EditorEligibilityRule[] } {
    if (eligibilityRules.op === 'and' || eligibilityRules.op === 'or') {
      const compositeOperator = new EditorEligibilityOperator({
        id: guid(),
        name: eligibilityRules?.description,
        affirmative: eligibilityRules?.affirmative,
        hideNestedDescriptions: eligibilityRules?.hideNestedDescriptions,
        ancestorHideNestedDescriptions: parentOperator !== null
          && (parentOperator.ancestorHideNestedDescriptions || parentOperator.hideNestedDescriptions),
        function: eligibilityRules.op,
        parent: parentOperator,
      });
      const operatorChildren: EditorEligibilityOperator[] = [];
      eligibilityRules.args.forEach(eligibilityRule => {
        const operatorWithRuleList = this.parseEligibilityOperatorFromEligibilityRules(
          eligibilityRule,
          compositeOperator,
          ruleList
        );
        operatorChildren.push(operatorWithRuleList.operator);
        ruleList = operatorWithRuleList.ruleList;
      });
      compositeOperator.children = operatorChildren;
      return { operator: compositeOperator, ruleList };
    }

    let rule: EditorEligibilityRule = null;
    let affirmative = true;

    switch (eligibilityRules.op) {
      case 'hasRole':
        rule = EditorEligibilityRuleHasRole.getFromSerializedValues(eligibilityRules.options);
        break;
      case 'hasRelation':
        rule = EditorEligibilityRuleHasRelation.getFromSerializedValues(eligibilityRules.options);
        break;
      case 'hasBookingItem':
        rule = EditorEligibilityRuleHasBookingItem.getFromSerializedValues(eligibilityRules.options);
        break;
      case 'hasBookingOption':
        rule = EditorEligibilityRuleHasBookingOption.getFromSerializedValues(eligibilityRules.options);
        break;
      case 'hasBookingType':
        rule = EditorEligibilityRuleHasBookingType.getFromSerializedValues(eligibilityRules.options);
        break;
      case 'hasEvent':
        rule = EditorEligibilityRuleHasEvent.getFromSerializedValues(eligibilityRules.options);
        break;
      case 'hasEventCount':
        rule = EditorEligibilityRuleHasEventCount.getFromSerializedValues(eligibilityRules.options);
        break;
      case 'hasUserField':
        switch (eligibilityRules.options.userFieldType) {
          case 'string':
            rule = EditorEligibilityRuleHasStringUserField.getFromSerializedValues(eligibilityRules.options);
            break;
          case 'date':
            rule = EditorEligibilityRuleHasDateUserField.getFromSerializedValues(eligibilityRules.options);
            break;
          case 'discrete':
            rule = EditorEligibilityRuleHasDiscreteUserField.getFromSerializedValues(eligibilityRules.options);
            break;
        }
        break;
      case 'isRawOperator':
        rule = EditorEligibilityRuleIsRawOperator.getFromSerializedValues(eligibilityRules.options);
        break;
    }

    if (rule !== null) {
      affirmative = eligibilityRules.affirmative;
    }

    if (rule === null) {
      if (
        EditorEligibilityRuleHasRelation.matchesTypeOfLegacySerializedValues(eligibilityRules)
        && typeof(eligibilityRules.args[0].options.value) !== 'string'
      ) {
        return this.parseEligibilityOperatorFromEligibilityRules({
          op: 'or',
          description: eligibilityRules.description,
          args: eligibilityRules.args[0].options.value.map(relation =>
            ({
              args: [
                {
                  options: { value: relation },
                  op: 'const'
                }, {
                  options: { user: 'owner' },
                  op: 'fetchUserRelations'
                }
              ],
              op: eligibilityRules.op
            })
          )
        },
        parentOperator,
        ruleList
        );
      }

      if (EditorEligibilityRuleHasRole.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasRole.getFromLegacySerializedValues(eligibilityRules);
      } else if (EditorEligibilityRuleHasRelation.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasRelation.getFromLegacySerializedValues(eligibilityRules);
      } else if (EditorEligibilityRuleHasBookingItem.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasBookingItem.getFromLegacySerializedValues(eligibilityRules);
      } else if (EditorEligibilityRuleHasBookingOption.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasBookingOption.getFromLegacySerializedValues(eligibilityRules);
      } else if (EditorEligibilityRuleHasBookingType.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasBookingType.getFromLegacySerializedValues(eligibilityRules);
      } else if (EditorEligibilityRuleHasEvent.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasEvent.getFromLegacySerializedValues(eligibilityRules);
      } else if (EditorEligibilityRuleHasEventCount.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasEventCount.getFromLegacySerializedValues(eligibilityRules);
      } else if (EditorEligibilityRuleHasDiscreteUserField.matchesTypeOfLegacySerializedValues(eligibilityRules)) {
        rule = EditorEligibilityRuleHasDiscreteUserField.getFromLegacySerializedValues(eligibilityRules);
      }

      if (rule !== null) {
        switch (rule.className) {
          case 'HasRole':
          case 'HasRelation':
          case 'HasDiscreteUserField':
            affirmative = eligibilityRules.op !== 'nin';
            break;
          case 'HasBookingItem':
          case 'HasBookingOption':
          case 'HasBookingType':
          case 'HasEvent':
            if (eligibilityRules.op === 'lt' && eligibilityRules?.args?.[1]?.options?.value === 1) {
              affirmative = false;
            } else if (eligibilityRules.op === 'lte' && eligibilityRules?.args?.[1]?.options?.value === 0) {
              affirmative = false;
            } else if (eligibilityRules.op === 'gt' && eligibilityRules?.args?.[1]?.options?.value === 0) {
              affirmative = true;
            } else if (eligibilityRules.op === 'gte' && eligibilityRules?.args?.[1]?.options?.value === 1) {
              affirmative = true;
            } else {
              throw new Error(`Can not understand a ${rule.className} rule.`);
            }
            break;
        }
      }
    }

    if (rule === null) {
      rule = EditorEligibilityRuleIsRawOperator.getFromLegacySerializedValues(eligibilityRules);
    }

    return {
      operator: new EditorEligibilityOperator({
        id: guid(),
        name: eligibilityRules?.description,
        affirmative,
        hideNestedDescriptions: eligibilityRules?.hideNestedDescriptions,
        ancestorHideNestedDescriptions: parentOperator !== null
          && (parentOperator.ancestorHideNestedDescriptions || parentOperator.hideNestedDescriptions),
        parent: parentOperator,
        ruleIndex: ruleList.length
      }),
      ruleList: [
        ...ruleList,
        rule
      ]
    };
  }

  getEligibilityFromEligibilityTree(eligibility: EditorEligibility, eligibilityTree: EditorEligibilityTree): EditorEligibility {
    return {
      ...eligibility,
      name: eligibilityTree.rootOperator.name ?? (
        eligibilityTree.rootOperator.hasRule() ? $localize `Unnamed rule` : $localize `Unnamed composite rule`
      ),
      rules: JSON.stringify(this.serializeEligibilityTree(eligibilityTree))
    };
  }

  serializeEligibilityTree(eligibilityTree: EditorEligibilityTree): any {
    return this.serializeEligibilityOperator(eligibilityTree.rootOperator, eligibilityTree.ruleList);
  }

  serializeEligibilityOperator(eligibilityOperator: EditorEligibilityOperator, ruleList: EditorEligibilityRule[]): any {
    const commonValues = {
      description: eligibilityOperator.name,
      affirmative: eligibilityOperator.affirmative
    };
    if (eligibilityOperator.hasRule()) {
      const eligibilityRule = ruleList[eligibilityOperator.ruleIndex];
      if (eligibilityRule === null) {
        return commonValues;
      } else {
        return {
          ...commonValues,
          op: eligibilityRule.operatorName,
          options: eligibilityRule.getSerializedValues()
        };
      }
    } else {
      return {
        ...commonValues,
        op: eligibilityOperator.function,
        hideNestedDescriptions: eligibilityOperator.hideNestedDescriptions,
        args: eligibilityOperator.children.map(
          childOperator => this.serializeEligibilityOperator(childOperator, ruleList)
        )
      };
    }
  }
}
